import { createTheme } from "@mui/material/styles"
import { baseCardTheme } from "./TeamBaseCardTheme"

// Full card theme for CardDesign5 (used in Card.js)
export const CardDesign5FloatingButtonTheme = createTheme(
    {
        ...baseCardTheme,
        // Custom card-specific values that aren't part of standard MUI theme
        card: {
            container: {
                maxWidth: {
                    xs: "95%",
                    sm: "400px",
                    md: "450px",
                    lg: "500px",
                    xl: "550px",
                },
                padding: "20px",
            },
            profileImage: {
                size: "168px",
                border: "4px solid #ffffff",
                bottom: "-85px",
                padding: "2px",
                margin: "-6px",
            },
            companyLogo: {
                size: "62px",
                bottom: "-75px",
                padding: "4px",
                margin: "-4px",
                rightOffset: "calc(50% - 105px)",
                leftOffset: "118px", // For left-aligned layout
            },
            portraitLogo: {
                size: "60px",
                top: "50px",
                right: "-50px",
            },
            content: {
                marginTop: {
                    portrait: "20px",
                    other: "85px",
                },
                padding: "20px",
            },
            typography: {
                nameSize: "1.5rem",
                nameHeight: "36px",
                nameLineHeight: "36px",
                nameMarginBottom: "4px",

                pronounSize: "17px",
                pronounHeight: "24.3px",
                pronounLineHeight: "24.31px",
                pronounMarginTop: "8px",

                jobTitleSize: "1.1875rem",
                jobTitleLineHeight: "1.6981rem",

                locationSize: "1rem",
                locationLineHeight: "1.9rem",

                bioSize: {
                    xs: "1rem",
                    sm: "1.2rem",
                    md: "1.5rem",
                },
                bioLineHeight: {
                    xs: "6vh",
                    sm: "7vh",
                    md: "22.5px",
                },
                bioMargin: {
                    top: "1.25rem",
                    bottom: {
                        xs: "0",
                        sm: "0.5rem",
                        md: "1rem",
                    },
                },
            },
            button: {
                fontSize: "1.1875rem",
                padding: "0.7rem 1rem",
                letterSpacing: "0.034rem",
                marginTop: "20px",
            },
            links: {
                marginTop: 2,
                itemMargin: 1.5,
                itemMarginBottom: 1,
                iconSize: "55px",
                iconBorderRadius: "14px",
                iconPadding: "5px",
                leftIconSize: "45px",
                leftIconBorderRadius: "7px",
                titleFontSize: "12px",
                leftTitleFontSize: "14px",
                titleMarginTop: "5px",
                iconMarginRight: 2,
                rowMarginBottom: 2,
                itemMaxWidth: "110px",
                itemPadding: "0 5px",
            },
            // CardDesign5 - Full card design with all styles centralized
            cardDesign5: {
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',

                // Color scheme - ALL COLORS CENTRALIZED
                colors: {
                    primary: "#FF7A00", // Default primary color
                    secondary: "#00796B", // Default secondary color
                    profileCard: "#006659", // Profile card background
                    white: "#fff",
                    black: "#000",
                    greyBackground: "#f6f6f6",
                    greyLight: "#f5f5f5",
                    greyText: "#999",
                    greyDark: "#666",
                    textDark: "rgb(31, 41, 55)",
                    textMedium: "rgb(107, 114, 128)",
                    textLight: "rgb(75, 85, 99)",

                    // Social media brand colors
                    facebook: "#1877F2",
                    twitter: "#1DA1F2",
                    linkedin: "#0077B5",
                    instagram: "#E4405F",
                    wechat: "#07C160",

                    // Transparency values
                    whiteTransparent: "rgba(0, 0, 0, 0.1)",
                    blackTransparent: "rgba(0, 0, 0, 0.15)",
                    blackTransparentLight: "rgba(0, 0, 0, 0.08)",
                    overlayLight: "rgba(0, 121, 107, 0.04)",
                },

                // Numeric values - ALL NUMBERS CENTRALIZED
                values: {
                    darkenAmount: 0.2,
                    lightenAmount: 0.15,
                    profileCardWidthLeft: "45%",
                    profileCardWidthRight: "55%",
                    addressMarginTop: 0.5,
                    profileCardOpacity: 0.9,
                    profileCardPronounOpacity: 0.8,
                    profileCardNameLineHeight: 1.2,
                    profileCardNameMarginBottom: 0.5,
                    profileCardJobTitleMarginBottom: 0.5,
                    tabContentPaddingBottom: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    actionButtonsGap: 2,
                    productItemMarginBottom: 2,
                    emptyMessagePadding: { x: 2, y: 4 },
                    sectionTitleMarginTop: 3,
                },

                // CSS properties - ALL CSS VALUES CENTRALIZED
                cssProperties: {
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    objectFit: "contain",
                    cursor: "pointer",
                    textTransform: "none",
                    fontFamily: '"Kumbh Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    transition: "all 0.2s ease-in-out",
                    wordBreak: "break-word",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textAlign: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    display: "flex",
                    position: "relative",
                    zIndex: { low: 1, medium: 10, high: 15, highest: 20 },
                    transparent: "transparent",
                    none: "none",
                },

                // Fallback text values - ALL TEXT CENTRALIZED
                fallbackText: {
                    fullName: "Full Name",
                    jobTitle: "Job Title",
                    companyInitial: "C",
                    jobTitleLabel: "JOB TITLE",
                    companyNameLabel: "COMPANY NAME",
                    addressLabel: "ADDRESS",
                    contactSectionTitle: "CONTACT",
                    aboutSectionTitle: "ABOUT",
                    phoneLabel: "PHONE",
                    twitterLabel: "X (TWITTER)",
                    noSocialLinks: "No social links added yet.",
                    noProducts: "No products or services added yet.",
                    saveContactButton: "Save Contact",
                    connectButton: "Connect",
                    companyLogoAlt: "Company Logo",
                },

                // Main container styles
                mainContainer: {
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: { xs: "95%", sm: "400px", md: "450px" },
                    margin: "auto",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    position: "relative",
                },

                // Header background styles - INCREASED HEIGHT
                headerBackground: {
                    height: { xs: "13.75rem", sm: "15rem", md: "16.25rem" },
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                },

                // Header content styles
                headerContent: {
                    padding: { xs: 1.5, sm: 2 },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                },

                // Company logo in header
                companyLogo: {
                    width: { xs: "2.5rem", sm: "2.8125rem", md: "3.125rem" },
                    height: { xs: "2.5rem", sm: "2.8125rem", md: "3.125rem" },
                    bgcolor: "#fff",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    border: "2px solid #fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                },

                // Company initial text
                companyInitial: {
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    fontFamily: "sans-serif",
                },

                // QR Code icon
                qrIcon: {
                    width: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    height: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    bgcolor: "#fff",
                    borderRadius: { xs: "0.375rem", sm: "0.5rem", md: "0.625rem" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1.5px solid #fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                },

                // QR icon inner
                qrIconInner: {
                    color: "#000",
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.25rem" },
                },

                // QR icon hover effect
                qrIconHover: {
                    "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                },

                // White background section - OPTIMIZED padding for Android-style floating buttons
                whiteBackground: {
                    bgcolor: "#fff",
                    minHeight: { xs: "25rem", sm: "28.125rem", md: "31.25rem" },
                    position: "relative",
                    borderTopLeftRadius: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    borderTopRightRadius: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                    marginTop: { xs: "-0.75rem", sm: "-0.875rem", md: "-1rem" },
                    paddingTop: { xs: "3.75rem", sm: "4.375rem", md: "5rem" },
                    // Optimized bottom padding for properly spaced floating buttons
                    paddingBottom: { xs: "6rem", sm: "6.5rem", md: "7rem" },
                    zIndex: 1,
                },

                // Profile card positioning and sizing - MOVED UP
                profileCard: {
                    position: "absolute",
                    // UPDATED: Moved up so top half is on cover photo, bottom half on white container
                    top: { xs: "-5rem", sm: "-5.5rem", md: "-6rem" },
                    left: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    right: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
                    height: { xs: "10rem", sm: "11rem", md: "12rem" },
                    borderRadius: { xs: "0.75rem", sm: "1rem", md: "1.25rem" },
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    display: "flex",
                    zIndex: 10,
                },

                // Profile photo section (left side)
                profilePhotoSection: {
                    width: "45%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    position: "relative",
                    bgcolor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },

                // Fallback avatar
                fallbackAvatar: {
                    width: { xs: "3rem", sm: "3.5rem", md: "4rem" },
                    height: { xs: "3rem", sm: "3.5rem", md: "4rem" },
                    border: "3px solid #fff",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },

                // Avatar image styles
                avatarImageStyles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                },

                // Profile card right section
                profileCardRightSection: {
                    width: "55%",
                    padding: { xs: 1.5, sm: 2, md: 2.5 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                },

                // Name typography in profile card
                profileCardName: {
                    fontWeight: "bold",
                    lineHeight: 1.2,
                    marginBottom: 0.5,
                    fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                    wordBreak: "break-word",
                },

                // Job title typography in profile card
                profileCardJobTitle: {
                    opacity: 0.9,
                    marginBottom: 0.5,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    wordBreak: "break-word",
                },

                // Pronoun typography in profile card
                profileCardPronoun: {
                    opacity: 0.8,
                    fontSize: { xs: "0.625rem", sm: "0.75rem" },
                },

                // Secondary job title below profile card - ADJUSTED FOR NEW POSITIONING
                secondaryJobTitle: {
                    paddingTop: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                    paddingBottom: 1,
                    textAlign: "center",
                },

                secondaryJobTitleText: {
                    fontWeight: 500,
                    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                },

                // Tabs container
                tabsContainer: {
                    backgroundColor: "#f6f6f6",
                    borderRadius: "8px",
                    padding: { xs: "0.25rem", sm: "0.375rem", md: "0.5rem" },
                    margin: { xs: "0.25rem 1.5rem", sm: "0.25rem 2rem", md: "0.375rem 2.5rem" },
                },

                // Tabs styling - OPTIMIZED: Smaller font & better spacing
                tabs: {
                    "& .MuiTabs-indicator": {
                        display: "none",
                    },
                    "& .MuiTab-root": {
                        backgroundColor: "transparent",
                        color: "#000000",
                        borderRadius: "8px",
                        margin: "2px",
                        textTransform: "none",
                        fontFamily: '"Kumbh Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 600,
                        // UPDATED: Smaller font sizes
                        fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                        minWidth: "auto",
                        flexGrow: 1,
                        whiteSpace: "nowrap",
                        padding: { xs: "0.5rem 0.25rem", sm: "0.625rem 0.5rem", md: "0.75rem 0.75rem" },
                        "&.Mui-selected": {
                            backgroundColor: "#ffffff",
                            color: "rgb(31, 41, 55)",
                            fontWeight: 600,
                        },
                        "&:hover": {
                            backgroundColor: "#ffffff",
                            color: "rgb(31, 41, 55)",
                            transform: "scale(1.02)",
                        },
                    },
                    "& .MuiTabs-flexContainer": {
                        gap: { xs: "0.125rem", sm: "0.25rem", md: "0.375rem" },
                    },
                    // OPTIMIZED: Smaller scroll buttons and better space utilization
                    "& .MuiTabs-scrollButtons": {
                        // UPDATED: Reduced width to give more space to tabs
                        width: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                        // UPDATED: Reduced padding
                        paddingX: { xs: "0.125rem", sm: "0.25rem", md: "0.375rem" },
                        color: "#666",
                        opacity: 0.7,
                        borderRadius: "6px",
                        transition: "all 0.2s ease-in-out",
                        "&.Mui-disabled": {
                            opacity: 0.3,
                        },
                        "&:hover": {
                            opacity: 1,
                            backgroundColor: "rgba(0,0,0,0.04)",
                            transform: "scale(1.05)",
                        },
                        "& .MuiSvgIcon-root": {
                            // UPDATED: Smaller arrow icons
                            fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                        }
                    },
                    "& .MuiTabs-scroller": {
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": {
                            display: "none", // Hide scrollbar for cleaner look
                        },
                        scrollbarWidth: "none", // Firefox
                    },
                    // Scrollable mode adjustments
                    "&[data-scrollable='true']": {
                        "& .MuiTabs-flexContainer": {
                            // UPDATED: Reduced gap and padding for more tab space
                            gap: { xs: "0.125rem", sm: "0.25rem" },
                            paddingX: { xs: "0.25rem", sm: "0.5rem", md: "0.75rem" },
                        },
                        "& .MuiTab-root": {
                            flexGrow: 0,
                            flexShrink: 0,
                            minWidth: "auto",
                            // UPDATED: Optimized padding for better space usage
                            paddingX: { xs: "0.5rem", sm: "0.75rem", md: "1rem" },
                        }
                    }
                },

                // Individual tab styling
                tabItem: {
                    minWidth: { xs: "3rem", sm: "4rem", md: "5rem" },
                    flex: "0 1 auto",
                },

                tabItemLarge: {
                    minWidth: { xs: "6rem", sm: "8rem", md: "10rem" },
                    flex: "0 1 auto",
                },

                // Tab content
                tabContent: {
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingBottom: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                },

                // Contact tab container
                contactTabContainer: {
                    padding: 0,
                },

                // Contact item (grey containers)
                contactItem: {
                    backgroundColor: "#f6f6f6",
                    margin: { xs: "0.25rem 1.5rem", sm: "0.375rem 2rem", md: "0.5rem 2.5rem" },
                    borderRadius: "8px",
                    padding: { xs: "0.5rem", sm: "0.625rem", md: "0.75rem" },
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                },

                // Contact item for address (different alignment)
                contactItemAddress: {
                    backgroundColor: "#f6f6f6",
                    margin: { xs: "0.25rem 1.5rem", sm: "0.375rem 2rem", md: "0.5rem 2.5rem" },
                    borderRadius: "8px",
                    padding: { xs: "0.5rem", sm: "0.625rem", md: "0.75rem" },
                    display: "flex",
                    alignItems: "flex-start",
                    cursor: "pointer",
                },

                // Contact icon
                contactIcon: {
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 2,
                },

                // Contact icon for address (with top margin)
                contactIconAddress: {
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 2,
                    marginTop: 0.5,
                },

                // Contact icon for larger social icons
                contactIconLarge: {
                    width: 32,
                    height: 32,
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 2,
                },

                // Contact label
                contactLabel: {
                    color: "#666",
                    display: "block",
                    fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.75rem" },
                    fontWeight: 500,
                    letterSpacing: "0.03em",
                },

                // Contact value - MAIN CARD: WITH WRAPPING FOR ALL CONTACT TYPES
                contactValue: {
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                    // MAIN CARD SPECIFIC: Allow wrapping for complete visibility
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4,
                    maxWidth: "100%",
                },

                // Section title
                sectionTitle: {
                    color: "#666",
                    textTransform: "uppercase",
                    fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                    letterSpacing: "0.05em",
                    fontWeight: 600,
                    marginTop: 3,
                    textAlign: "center",
                },

                // Product section
                productSection: {
                    padding: 0,
                },

                // Product section content
                productSectionContent: {
                    paddingLeft: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    paddingRight: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                },

                // Product item margin
                productItem: {
                    marginBottom: 2,
                },

                // Empty state message
                emptyMessage: {
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 2,
                    color: "#999",
                    fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
                },

                // Action buttons container - CLEAN (no background, used for button styling only)
                actionButtons: {
                    // Just button styling properties
                    display: "flex",
                    gap: 2,
                    transition: "all 0.2s ease-in-out",
                    boxSizing: "border-box",
                },

                // Save button
                saveButton: {
                    flex: 1,
                    borderRadius: "8px",
                    paddingY: { xs: 1.5, sm: 1.75, md: 2 },
                    color: "#fff",
                },

                // Connect button
                connectButton: {
                    flex: 1,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    paddingY: 1,
                    paddingX: 2,
                    color: "#fff",
                },

                // Icon styles
                iconStyles: {
                    color: "#fff",
                    fontSize: "1rem",
                },

                // Social icon styles
                socialIconStyles: {
                    color: "#fff",
                    fontSize: "1.25rem",
                },

                // Social icon image
                socialIconImage: {
                    width: "100%",
                    height: "100%",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: "3px",
                },

                // Hover effects
                hoverTransform: {
                    "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                },

                // Map styles - MOVED INSIDE cardDesign5 OBJECT
                map: {
                    container: {
                        height: 200,
                        border: '1px solid #ddd',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                    image: {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    },
                    overlay: {
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#1976d2',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }
                },
            },
        },
    },
    baseCardTheme,
)

